import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import {
  DATE_FORMAT,
  LoadingEvent,
  useDataProvider,
  useSnackbar,
  useUserAccess,
} from '@ui-framework/index';
import FormErrorsArray from '@ui-framework/components/AdvancedForms/FormErrorsArray';
import Calendar from '@product-framework/components/Calendar';
import LiveCalendarLib from './utils/LiveCalendar';
import ScheduleUpdateDialog from './components/ScheduleUpdateDialog';
import SpecialScheduleInfo from './components/LiveCalendar/SpecialScheduleInfo';
import DateCellContent from './components/LiveCalendar/DateCellContent';

function LiveCalendar({ serviceProviderId, locationId = null }) {
  const currentMonthInView = dayjs(new Date()).startOf('month');

  const snackbar = useSnackbar();
  const userAccess = useUserAccess();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, get, post, remove, getApiUrl } = useDataProvider(provider);
  const [loading, setLoading] = useState(true);
  const [calendarData, setCalendarData] = useState({});
  const [monthInView, setMonthInView] = useState(currentMonthInView);
  const [errors, setErrors] = useState(null);

  const [selectedDay, setSelectedDay] = useState(null);

  const readOnly = userAccess.isUserReadonly();

  const commonParams = {
    snackbar,
    put,
    get,
    post,
    remove,
    getApiUrl,
    pageId: serviceProviderId,
  };

  const fetchLiveCalendarData = async () => {
    setLoading(true);
    setErrors([]);
    const fetchFromDate = dayjs(monthInView).format(DATE_FORMAT.CODE_FORMAT);
    const liveCalendarResponse = await LiveCalendarLib.fetch({
      params: commonParams,
      dated: fetchFromDate,
      timeSpan: 'month',
      locationId,
    });
    if (liveCalendarResponse?.ok) {
      setCalendarData(liveCalendarResponse?.data);
    } else {
      setErrors(liveCalendarResponse.errors);
      setCalendarData({});
    }
    setLoading(false);
  };

  const onClose = changed => {
    setSelectedDay(null);
    if (changed) {
      fetchLiveCalendarData();
    }
  };

  useEffect(() => {
    fetchLiveCalendarData();
  }, [monthInView]);

  if (loading) {
    return (
      <LoadingEvent>
        <FormattedMessage
          id="LOADING_CALENDAR_DATA"
          defaultMessage="Loading calendar data"
        />
        ...
      </LoadingEvent>
    );
  }

  return (
    <Box sx={{ mb: 2, mt: 2 }}>
      <FormErrorsArray errors={errors || []} />
      <Calendar
        isClickable={({ cellDate, currentDate }) => {
          const isPast = cellDate.isBefore(currentDate, 'day');
          return !readOnly && !isPast;
        }}
        // Information to be fed to each date cell
        getDateCellValue={({ value, textDate }) => {
          const dayData = value.days?.find(d => {
            return d.date === textDate;
          });
          return dayData;
        }}
        monthInView={monthInView}
        value={calendarData}
        onChange={data => setMonthInView(data)}
        onDayClick={({ cellDateValue }) => {
          setSelectedDay(cellDateValue);
        }}
        DateContainerComponent={{
          rightComponent: SpecialScheduleInfo,
        }}
        DayCellContentComponent={{
          component: DateCellContent,
        }}
      />
      {selectedDay && (
        <ScheduleUpdateDialog
          locationId={locationId}
          serviceProviderId={serviceProviderId}
          defaultValues={{
            date: selectedDay?.date,
            slotsToUpdate: Array.isArray(selectedDay?.slots)
              ? selectedDay?.slots?.filter(s => s?.available === true)
              : [],
          }}
          open={Boolean(selectedDay)}
          onClose={onClose}
        />
      )}
    </Box>
  );
}

export default LiveCalendar;
